import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Button,
  Input,
  Table,
  Space,
  Typography,
  message,
  Modal,
  Form,
  InputNumber,
  Spin,
  Dropdown,
} from 'antd';
import {
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  SaveOutlined,
  EyeOutlined,
  MoonOutlined,
  SunOutlined,
  ReloadOutlined,
  EditOutlined,
  LoadingOutlined,
  ArrowLeftOutlined,
  ColumnWidthOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Currency, CurrencyRequest, ExchangeOffice } from '../types';
import { defaultCurrencies } from '../utils/defaultCurrencies';
import * as api from '../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const AdminOfficeManage: React.FC = () => {
  const { officeId } = useParams<{ officeId: string }>();
  const navigate = useNavigate();
  const { theme, isDark, toggleTheme } = useTheme();

  const [office, setOffice] = useState<ExchangeOffice | null>(null);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [modifiedCurrencyIds, setModifiedCurrencyIds] = useState<Set<number>>(new Set());
  const [updatingCurrencyId, setUpdatingCurrencyId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingCurrencies, setSavingCurrencies] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [form] = Form.useForm();
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const id = officeId ? parseInt(officeId, 10) : NaN;

  const openDisplayLink = (mode: 1 | 2 | 3 | 4, linkVersion: 'old' | 'new') => {
    const oldQueries: Record<1 | 2 | 3 | 4, string> = {
      1: 'h=1&theme=dark',
      2: 'h=1&theme=light',
      3: 'v=1&theme=dark',
      4: 'v=1&theme=light',
    };
    const query = linkVersion === 'old' ? oldQueries[mode] : `m=${mode}`;
    window.open(`/display/${id}?${query}`, '_blank');
  };

  useEffect(() => {
    if (!officeId || isNaN(id)) {
      message.error('معرف المكتب غير صالح');
      navigate('/admin');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeId, id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [officeRes, list]: [{data: ExchangeOffice, lastUpdatedAt: string}, {data: Currency[], lastUpdatedAt: string}] = await Promise.all([
        api.getOffice(id) as unknown as Promise<{data: ExchangeOffice, lastUpdatedAt: string}>,
        api.getPublicCurrencies(id) as Promise<{data: Currency[], lastUpdatedAt: string}>,
      ]);
      setOffice(officeRes.data);
      const sorted = [...list.data].sort((a, b) => a.order - b.order);
      setCurrencies(sorted);
      setLastUpdate(list.lastUpdatedAt);
      setModifiedCurrencyIds(new Set());
    } catch (e: any) {
      message.error('فشل في تحميل البيانات: ' + (e.message || ''));
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const reloadCurrencies = async () => {
    try {
      const list: {data: Currency[], lastUpdatedAt: string} = await api.getPublicCurrencies(id);
      setCurrencies([...list.data].sort((a, b) => a.order - b.order));
      setLastUpdate(list.lastUpdatedAt);
      setModifiedCurrencyIds(new Set());
    } catch {
      /* ignore */
    }
  };

  const markCurrencyModified = (currencyId: number) => {
    setModifiedCurrencyIds((prev) => new Set(prev).add(currencyId));
  };

  const isCurrencyModified = (currencyId: number) => modifiedCurrencyIds.has(currencyId);

  const updateCurrencyField = (recordId: number, field: string, value: unknown) => {
    setCurrencies((prev) =>
      prev.map((c) => (c.id === recordId ? { ...c, [field]: value } : c))
    );
    if (recordId !== 0) markCurrencyModified(recordId);
  };

  /** Build CurrencyRequest from Currency for API (units always 1). */
  const currencyToRequest = (c: Currency): CurrencyRequest => ({
    exchangeOfficeId: id,
    flag: c.flag ?? '',
    nameEn: c.nameEn ?? '',
    nameAr: c.nameAr ?? '',
    iso: c.iso ?? '',
    units: '1',
    buy: typeof c.buy === 'string' ? parseFloat(c.buy) || 0 : Number(c.buy) || 0,
    sell: typeof c.sell === 'string' ? parseFloat(c.sell) || 0 : Number(c.sell) || 0,
    order: c.order ?? 0,
  });

  const handleAddCurrency = () => {
    const newCurrency: Currency = {
      id: 0,
      exchangeOfficeId: id,
      flag: '',
      nameEn: '',
      nameAr: '',
      iso: '',
      units: '1',
      buy: 0,
      sell: 0,
      order: currencies.length,
    };
    setEditingCurrency(newCurrency);
    form.setFieldsValue(newCurrency);
    setIsModalVisible(true);
  };

  const handleEditCurrency = (currency: Currency) => {
    setEditingCurrency(currency);
    form.setFieldsValue(currency);
    setIsModalVisible(true);
  };

  const handleDeleteCurrency = async (currencyId: number) => {
    try {
      await api.deleteCurrencyForOffice(id, currencyId);
      message.success('تم حذف العملة');
      await reloadCurrencies();
    } catch (e: any) {
      message.error('فشل في حذف العملة: ' + (e.message || ''));
    }
  };

  const handleMoveCurrency = async (index: number, direction: 'up' | 'down') => {
    const next = [...currencies];
    let a: Currency | null = null;
    let b: Currency | null = null;

    if (direction === 'up' && index > 0) {
      [next[index], next[index - 1]] = [next[index - 1], next[index]];
      next[index].order = index;
      next[index - 1].order = index - 1;
      a = next[index];
      b = next[index - 1];
    } else if (direction === 'down' && index < next.length - 1) {
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      next[index].order = index;
      next[index + 1].order = index + 1;
      a = next[index];
      b = next[index + 1];
    }

    setCurrencies(next);

    if (a && b && a.id !== 0 && b.id !== 0) {
      try {
        await Promise.all([
          api.updateCurrencyForOffice(id, a.id, currencyToRequest(a)),
          api.updateCurrencyForOffice(id, b.id, currencyToRequest(b)),
        ]);
        message.success('تم تحديث الترتيب');
        await reloadCurrencies();
      } catch (e: any) {
        message.error('فشل في تحديث الترتيب: ' + (e.message || ''));
        await reloadCurrencies();
      }
    }
  };

  const handleSaveCurrency = async () => {
    try {
      const values = await form.validateFields();
      if (!editingCurrency) return;

      const req: CurrencyRequest = {
        exchangeOfficeId: id,
        flag: values.flag,
        nameEn: values.nameEn,
        nameAr: values.nameAr,
        iso: values.iso,
        units: '1',
        buy: values.buy,
        sell: values.sell,
        order: editingCurrency.order,
      };

      if (editingCurrency.id === 0) {
        await api.createCurrencyForOffice(id, req);
        message.success('تم إضافة العملة');
      } else {
        await api.updateCurrencyForOffice(id, editingCurrency.id, req);
        message.success('تم تحديث العملة');
      }

      await reloadCurrencies();
      setIsModalVisible(false);
    } catch (e: any) {
      if (e.errorFields) return;
      message.error('فشل في حفظ العملة: ' + (e.message || ''));
    }
  };

  const handleUpdateCurrency = async (currency: Currency) => {
    if (currency.id === 0) return;
    const latest = currencies.find((x) => x.id === currency.id) ?? currency;
    setUpdatingCurrencyId(currency.id);
    try {
      await api.updateCurrencyForOffice(id, latest.id, currencyToRequest(latest));
      message.success('تم تحديث العملة');
      await reloadCurrencies();
    } catch (e: any) {
      message.error('فشل في تحديث العملة: ' + (e.message || ''));
    } finally {
      setUpdatingCurrencyId(null);
    }
  };

  const handleSaveAllCurrencies = async () => {
    setSavingCurrencies(true);
    try {
      const requests: CurrencyRequest[] = currencies.map((c, idx) => ({
        exchangeOfficeId: id,
        flag: c.flag,
        nameEn: c.nameEn,
        nameAr: c.nameAr,
        iso: c.iso,
        units: '1',
        buy: c.buy,
        sell: c.sell,
        order: idx,
      }));
      await api.createCurrenciesForOffice(id, requests);
      message.success('تم حفظ جميع العملات');
      await reloadCurrencies();
    } catch (e: any) {
      message.error('فشل في حفظ العملات: ' + (e.message || ''));
    } finally {
      setSavingCurrencies(false);
    }
  };

  // تعيين القيم الافتراضية وحفظها مباشرة بدون الحاجة لزر حفظ
  const handleResetDefaults = async () => {
    setSavingCurrencies(true);
    try {
      const requests: CurrencyRequest[] = defaultCurrencies.map((c, idx) => ({
        exchangeOfficeId: id,
        flag: c.flag,
        nameEn: c.nameEn,
        nameAr: c.nameAr,
        iso: c.iso,
        units: '1',
        buy: c.buy,
        sell: c.sell,
        order: idx,
      }));
      await api.createCurrenciesForOffice(id, requests);
      message.success('تم تعيين وحفظ القيم الافتراضية بنجاح');
      await reloadCurrencies();
    } catch (e: any) {
      message.error('فشل في تعيين القيم الافتراضية: ' + (e.message || ''));
    } finally {
      setSavingCurrencies(false);
    }
  };

  const officeName = office?.name ?? currencies[0]?.exchangeOffice?.name ?? '...';

  const columns = [
    {
      title: 'FLAG',
      dataIndex: 'flag',
      key: 'flag',
      width: 100,
      render: (text: string, r: Currency) => (
        <Input
          value={text ?? ''}
          onChange={(e) => updateCurrencyField(r.id, 'flag', e.target.value)}
          placeholder="us, gb, etc."
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: 'NAME (EN)',
      dataIndex: 'nameEn',
      key: 'nameEn',
      render: (text: string, r: Currency) => (
        <Input
          value={text ?? ''}
          onChange={(e) => updateCurrencyField(r.id, 'nameEn', e.target.value)}
        />
      ),
    },
    {
      title: 'NAME (AR)',
      dataIndex: 'nameAr',
      key: 'nameAr',
      render: (text: string, r: Currency) => (
        <Input
          value={text ?? ''}
          onChange={(e) => updateCurrencyField(r.id, 'nameAr', e.target.value)}
          style={{ direction: 'rtl' }}
        />
      ),
    },
    {
      title: 'ISO',
      dataIndex: 'iso',
      key: 'iso',
      width: 100,
      render: (text: string, r: Currency) => (
        <Input
          value={text ?? ''}
          onChange={(e) => updateCurrencyField(r.id, 'iso', e.target.value)}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: 'BUY',
      dataIndex: 'buy',
      key: 'buy',
      width: 120,
      render: (val: number | string, r: Currency, rowIndex: number) => (
        <div className="admin-rate-cell admin-rate-buy" style={{ animationDelay: `${rowIndex * 0.15}s` }}>
          <InputNumber
            value={typeof val === 'string' ? parseFloat(val) : val}
            onChange={(v) => updateCurrencyField(r.id, 'buy', v ?? 0)}
            min={0}
            step={0.01}
            style={{ width: 110 }}
          />
        </div>
      ),
    },
    {
      title: 'SELL',
      dataIndex: 'sell',
      key: 'sell',
      width: 120,
      render: (val: number | string, r: Currency, rowIndex: number) => (
        <div className="admin-rate-cell admin-rate-sell" style={{ animationDelay: `${rowIndex * 0.15}s` }}>
          <InputNumber
            value={typeof val === 'string' ? parseFloat(val) : val}
            onChange={(v) => updateCurrencyField(r.id, 'sell', v ?? 0)}
            min={0}
            step={0.01}
            style={{ width: 110 }}
          />
        </div>
      ),
    },
    {
      title: 'الإجراءات',
      key: 'actions',
      width: 200,
      render: (_: unknown, r: Currency, index: number) => (
        <Space>
          {isCurrencyModified(r.id) && r.id !== 0 && (
            <Button
              type="primary"
              icon={updatingCurrencyId === r.id ? <LoadingOutlined /> : <EditOutlined />}
              onClick={() => handleUpdateCurrency(r)}
              disabled={updatingCurrencyId === r.id}
              size="small"
              style={{ background: theme.success, borderColor: theme.success }}
            >
              تحديث
            </Button>
          )}
          <Button
            icon={<ArrowUpOutlined />}
            onClick={() => handleMoveCurrency(index, 'up')}
            disabled={index === 0}
            size="small"
          />
          <Button
            icon={<ArrowDownOutlined />}
            onClick={() => handleMoveCurrency(index, 'down')}
            disabled={index === currencies.length - 1}
            size="small"
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteCurrency(r.id)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: theme.background, color: theme.text }}>
      <Header
        style={{
          background: theme.surface,
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 24px',
        }}
      >
        <Title level={3} style={{ color: theme.text, margin: 0 }}>
          إدارة المكتب • {officeName}
        </Title>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin')}
            style={{ background: theme.surfaceLight, borderColor: theme.border, color: theme.text }}
          >
            العودة للإدارة
          </Button>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                { key: 'old', label: 'الرابط القديم', onClick: () => openDisplayLink(1, 'old') },
                { key: 'new', label: 'الرابط الجديد (m=1)', onClick: () => openDisplayLink(1, 'new') },
              ],
            }}
          >
            <Button
              icon={<ColumnWidthOutlined />}
              style={{ background: theme.primary, borderColor: theme.primary, color: '#fff' }}
            >
              عرضي غامق
            </Button>
          </Dropdown>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                { key: 'old', label: 'الرابط القديم', onClick: () => openDisplayLink(2, 'old') },
                { key: 'new', label: 'الرابط الجديد (m=2)', onClick: () => openDisplayLink(2, 'new') },
              ],
            }}
          >
            <Button
              icon={<ColumnWidthOutlined />}
              style={{ background: theme.primary, borderColor: theme.primary, color: '#fff' }}
            >
              عرضي فاتح
            </Button>
          </Dropdown>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                { key: 'old', label: 'الرابط القديم', onClick: () => openDisplayLink(3, 'old') },
                { key: 'new', label: 'الرابط الجديد (m=3)', onClick: () => openDisplayLink(3, 'new') },
              ],
            }}
          >
            <Button
              icon={<MenuOutlined />}
              style={{ background: theme.primary, borderColor: theme.primary, color: '#fff' }}
            >
              طولي غامق
            </Button>
          </Dropdown>
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                { key: 'old', label: 'الرابط القديم', onClick: () => openDisplayLink(4, 'old') },
                { key: 'new', label: 'الرابط الجديد (m=4)', onClick: () => openDisplayLink(4, 'new') },
              ],
            }}
          >
            <Button
              icon={<MenuOutlined />}
              style={{ background: theme.primary, borderColor: theme.primary, color: '#fff' }}
            >
              طولي فاتح
            </Button>
          </Dropdown>
          <Button
            icon={isDark ? <SunOutlined /> : <MoonOutlined />}
            onClick={toggleTheme}
            style={{ background: theme.surfaceLight, borderColor: theme.border, color: theme.text }}
          >
            {isDark ? 'فاتح' : 'داكن'}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadData}
            style={{ background: theme.info, borderColor: theme.info, color: '#fff' }}
          >
            تحديث
          </Button>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: theme.background }}>
        <Spin spinning={loading}>
          <Card
            title={
              <Text style={{ color: theme.text, fontWeight: 'bold' }}>
                العملات — إضافة / تعديل / حذف / ترتيب
              </Text>
            }
            style={{ background: theme.surface, borderColor: theme.border }}
            headStyle={{ background: theme.surfaceLight, borderBottom: `1px solid ${theme.border}` }}
            bodyStyle={{ background: theme.surface }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Space wrap>
                <Button icon={<PlusOutlined />} onClick={handleAddCurrency} type="primary">
                  إضافة عملة
                </Button>
                <Button
                  onClick={handleResetDefaults}
                  loading={savingCurrencies}
                  style={{ background: theme.warning, borderColor: theme.warning, color: '#fff' }}
                >
                  القيم الافتراضية
                </Button>
              </Space>
              <Table
                dataSource={currencies}
                columns={columns}
                rowKey="id"
                pagination={false}
                scroll={{ x: 900 }}
                style={{ background: theme.background }}
              />
            </Space>
          </Card>
        </Spin>
      </Content>

      <style>{`
        @keyframes adminWaveBlinkBuy {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(-4px); opacity: 0.85; }
        }
        @keyframes adminWaveBlinkSell {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(-4px); opacity: 0.85; }
        }
        .admin-rate-cell.admin-rate-buy { animation: adminWaveBlinkBuy 2.5s ease-in-out infinite; }
        .admin-rate-cell.admin-rate-sell { animation: adminWaveBlinkSell 2.5s ease-in-out infinite; }
      `}</style>
      <Modal
        title={editingCurrency?.id === 0 ? 'إضافة عملة جديدة' : 'تعديل العملة'}
        open={isModalVisible}
        onOk={handleSaveCurrency}
        onCancel={() => setIsModalVisible(false)}
        okText="حفظ"
        cancelText="إلغاء"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="flag" label="رمز العلم" rules={[{ max: 255 }]}>
            <Input placeholder="us, gb, eu, sa, etc." />
          </Form.Item>
          <Form.Item
            name="nameEn"
            label="الاسم بالإنجليزية"
            rules={[{ required: true, message: 'مطلوب' }, { max: 255 }]}
          >
            <Input placeholder="US Dollar" />
          </Form.Item>
          <Form.Item
            name="nameAr"
            label="الاسم بالعربية"
            rules={[{ required: true, message: 'مطلوب' }, { max: 255 }]}
          >
            <Input placeholder="دولار أمريكي" style={{ direction: 'rtl' }} />
          </Form.Item>
          <Form.Item
            name="iso"
            label="رمز العملة (ISO)"
            rules={[{ required: true, message: 'مطلوب' }, { max: 10 }]}
          >
            <Input placeholder="USD" />
          </Form.Item>
          <Form.Item name="buy" label="سعر الشراء" rules={[{ required: true }]}>
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="0.00" />
          </Form.Item>
          <Form.Item name="sell" label="سعر البيع" rules={[{ required: true }]}>
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="0.00" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AdminOfficeManage;
